import React, { useState, useEffect } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useDataProvider,
  useEditController,
  SelectInput,
  EditProps,
  BooleanInput,
  FormDataConsumer,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography } from "@material-ui/core";
import EditableDataGrid from "./components/EditableAreaDatagrid";
import { AcademicYearResources, GetParam } from "utils/tools";
import { labelDataGridStyles } from "utils/style";
import moment from "moment";
import CustomEditToolbar from "components/CustomEditToolbar";
import { useField } from "react-final-form";
import CustomDatePickerInput from "components/CustomDatePickerInput";
import { reject } from "lodash";
import TbfCodeInput from "./components/TbfCodeInput";
import { validateRateChartCreation } from "./validation";
import CustomDateRangeInput from "../../components/CustomDateRangeInput";
import EditWrapper from "../../components/EditWrapper";
import PermissionLoading from "../../components/PermissionLoading";
import PrevRateChartInputField from "./components/PrevRateChartInputField";

interface CustomReferenceInputProps {
  param: any;
  disableForm: boolean;
  setIdAcademicYear: any;
}

const CustomReferenceInput: React.FC<CustomReferenceInputProps> = ({
  param,
  disableForm,
  setIdAcademicYear,
}) => {
  const startedAt = useField("startedAt");
  const endedAt = useField("endedAt");
  const invoiceIssuedAt = useField("invoiceIssuedAt");

  return (
    <ReferenceInput
      label="Select Academic Year"
      source="academicYearId"
      reference="academicYears"
      validate={[required()]}
      filter={{ SchoolId: Number(param.get("schoolId")) }}
      onChange={({ target }) => {
        setIdAcademicYear(target.value);
        startedAt.input.onChange("");
        endedAt.input.onChange("");
        invoiceIssuedAt.input.onChange("");
      }}
      disabled={disableForm}
    >
      <SelectInput optionText="name" optionId="id" />
    </ReferenceInput>
  );
};

const RateChartEdit = (props: EditProps) => {
  const [areas, setAreas] = useState([]);
  const [rateCharts, setRateCharts] = useState<IRateChart[]>([]);
  const [idAcademicYear, setIdAcademicYear] = useState();
  const [status, setStatus] = useState<string>();
  const [disableForm, setDisableForm] = useState<boolean>(false);
  const [loadingPrevChart, setLoadingPrevChart] = useState(false);

  const param = GetParam();
  const provider = useDataProvider();
  const { record, loading } = useEditController(props);

  const breadcrumb = [
    { title: "Schools", link: `/schools` },
    { title: "Pricing", link: `/schools/${param.get("schoolId")}/show/5` },
    { title: "Edit", link: "" },
  ];

  //fetch area data for current school on first load
  useEffect(() => {
    provider
      .getList("areas", {
        filter: { SchoolId: Number(param.get("schoolId")) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "MaxDistanceInMeters",
          order: "ASC",
        },
      })
      .then((result) => {
        setAreas(result.data);
      });

    provider.getOne("rateCharts", { id: props.id }).then((result) => {
      setIdAcademicYear(result.data.academicYearId);
      setStatus(result.data.status);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== "active") {
      setDisableForm(true);
    } else {
      setDisableForm(false);
    }
  }, [status]);

  const transform = (data: any) => {
    data.rateChartItems = [
      ...data.oneTimeAWeekPricing,
      ...data.twoTimeAWeekPricing,
      ...data.threeTimeAWeekPricing,
      ...data.fourTimeAWeekPricing,
      ...data.fiveTimeAWeekPricing,
    ];

    delete data.oneTimeAWeekPricing;
    delete data.twoTimeAWeekPricing;
    delete data.threeTimeAWeekPricing;
    delete data.fourTimeAWeekPricing;
    delete data.fiveTimeAWeekPricing;

    delete data.updatedAt;

    delete data.prevRateChartItems;

    return data;
  };

  const academicYearResources = AcademicYearResources(idAcademicYear);

  useEffect(() => {
    if (idAcademicYear !== undefined) {
      provider
        .getList("rateCharts", {
          filter: { AcademicYearId: Number(idAcademicYear) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then((result) => {
          setRateCharts(result.data as IRateChart[]);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idAcademicYear]);

  let excludeDates: IExcludeDates[] = [];
  let rateChartsWithoutSelected = reject(rateCharts, {
    id: Number(props.id),
  });

  rateChartsWithoutSelected.forEach((rateChart: IRateChart) => {
    let temp: IExcludeDates = {
      startDate: rateChart.startedAt,
      endDate: rateChart.endedAt,
    };

    excludeDates.push(temp);
  });

  const formatRecord = (res: any) => {
    if (res) {
      if (res.startedAt) {
        res.startedAt = moment(res.startedAt).format("YYYY-MM-DD");
      }

      if (res.endedAt) {
        res.endedAt = moment(res.endedAt).format("YYYY-MM-DD");
      }

      if (res.invoiceIssuedAt) {
        res.invoiceIssuedAt = moment(res.invoiceIssuedAt).format("YYYY-MM-DD");
      }

      res.prevRateChartItems = [];

      return res;
    }
  };

  if (loading) {
    return <PermissionLoading />;
  }

  return (
    <>
      <EditWrapper
        breadcrumb={breadcrumb}
        transform={transform}
        actions={false}
        {...props}
      >
        <SimpleForm
          record={formatRecord(record)}
          validate={(values: any) =>
            validateRateChartCreation(values, excludeDates)
          }
          toolbar={
            <CustomEditToolbar
              redirectLink={`/schools/${param.get("schoolId")}/show/5`}
              isDeleteWithConfirm
            />
          }
          name="manageRateChart"
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School & Academic Year</Typography>
            </Grid>

            <Grid item xs={3}>
              <ReferenceInput
                label="Select School"
                source="schoolId"
                reference="schools"
                validate={[required()]}
                disabled
              >
                <AutocompleteInput optionText="name" optionId="id" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <CustomReferenceInput
                param={param}
                disableForm={disableForm}
                setIdAcademicYear={setIdAcademicYear}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <TbfCodeInput />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Pricing Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput
                source="name"
                label="Pricing Name"
                validate={required()}
                disabled={disableForm}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={12}>
              <CustomDateRangeInput
                fromSource="startedAt"
                toSource="endedAt"
                fromLabel="Pricing Start Date"
                toLabel="Pricing End Date"
                parentStartDate={moment
                  .utc(academicYearResources?.startDate)
                  .format("yyyy-MM-DD")}
                fromMaxDate={moment
                  .utc(academicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
                toMaxDate={moment
                  .utc(academicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
                excludeDates={excludeDates}
                isDisabled={disableForm}
                gridItemWidth={3}
              />
            </Grid>

            <Grid item xs={3}>
              <CustomDatePickerInput
                name="invoiceIssuedAt"
                label="Invoice Issue Date"
                min={moment().format("Y-MM-DD")}
                disableValue={disableForm}
              />
            </Grid>

            {/* <Grid item xs={4}>
              <SelectInput
                source="isIncludeTax"
                label="Tax Included?"
                choices={[
                  { id: true, name: "Yes" },
                  { id: false, name: "No" },
                ]}
              />
            </Grid> */}

            <Grid item xs={12}>
              <Typography variant="h6">
                Area Pricing (Exclusive of GST)
              </Typography>
            </Grid>

            <Grid item xs={3}>
              <BooleanInput
                source="isDuplicateRateChartEnable"
                label="Enable Duplicate Ratechart"
                isDisabled={disableForm}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <FormDataConsumer>
              {({ formData }) =>
                formData.isDuplicateRateChartEnable && (
                  <SanitizedGrid
                    container
                    spacing={2}
                    style={{ marginTop: "-55px", padding: "10px" }}
                  >
                    <Grid item xs={3}>
                      <PrevRateChartInputField
                        rateChartItemSource="prevRateChartItems"
                        schoolId={Number(param.get("schoolId"))}
                        setLoading={setLoadingPrevChart}
                        excludeId={Number(props.id)}
                      />
                    </Grid>
                    <Grid item xs={9}></Grid>
                  </SanitizedGrid>
                )
              }
            </FormDataConsumer>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  1 Time a Week
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="oneTimeAWeekPricing"
                  frequency={1}
                  isDisabled={disableForm || loadingPrevChart}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  2 Time a Week
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="twoTimeAWeekPricing"
                  frequency={2}
                  isDisabled={disableForm || loadingPrevChart}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  3 Time a Week
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="threeTimeAWeekPricing"
                  frequency={3}
                  isDisabled={disableForm || loadingPrevChart}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  4 Time a Week
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="fourTimeAWeekPricing"
                  frequency={4}
                  isDisabled={disableForm || loadingPrevChart}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  5 Time a Week
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="fiveTimeAWeekPricing"
                  frequency={5}
                  isDisabled={disableForm || loadingPrevChart}
                />
              </Grid>
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </EditWrapper>
    </>
  );
};

export default RateChartEdit;
